import { Button, Group, Stack, useMantineTheme } from '@mantine/core';
import { modals } from '@mantine/modals';
import { IconPencil, IconTrash } from '@tabler/icons-react';
import { useState } from 'react';

import { MeasureSolutionResponseItem } from '@e-origin/contracts';

import { SearchBox, Table, TableActions, TableColumnProps } from '@components';
import { defaultMeasureSolutionCriteria, MeasureSolutionCriteria, useDeleteMeasureSolution, useMeasureSolutions } from '@hooks/measure-solutions';
import { EditMeasureSolution } from './EditMeasureSolution';

const openEditModal = (measureSolution?: MeasureSolutionResponseItem) => {
  modals.open({
    title: measureSolution ? 'Edit measure solution' : 'New measure solution',
    children: <EditMeasureSolution measureSolution={measureSolution} onHide={() => modals.closeAll()} />,
    size: 'lg',
  });
};

const openDeleteModal = (measureSolution: MeasureSolutionResponseItem, onConfirm: () => Promise<void>) => {
  modals.openConfirmModal({
    title: 'Delete measure solution',
    children: (
      <div>
        Are you sure you want to delete solution <strong>{measureSolution.solution}</strong> for HS code <strong>{measureSolution.hsCode}</strong>?
      </div>
    ),
    labels: { confirm: 'Delete', cancel: 'Cancel' },
    confirmProps: { color: 'red' },
    onConfirm: async () => {
      await onConfirm();
      modals.closeAll();
    },
  });
};

const buildColumns = (
  actionIconColor: string,
  onDelete: (measureSolution: MeasureSolutionResponseItem) => void,
): TableColumnProps<MeasureSolutionResponseItem>[] => [
  { name: 'HS code', cell: (row) => row.hsCode, sortable: true, selector: 'hsCode' },
  { name: 'Origin', cell: (row) => row.origin, sortable: true, selector: 'origin' },
  { name: 'Measure type', cell: (row) => row.measure, sortable: true, selector: 'measure' },
  { name: 'Condition group', cell: (row) => row.group, sortable: true, selector: 'group' },
  { name: 'Solution', cell: (row) => row.solution, sortable: true, selector: 'solution' },
  {
    name: 'Actions',
    sticky: 'right',
    cell: (row) => (
      <TableActions
        actions={[
          { iconComponent: <IconPencil color={actionIconColor} />, text: 'Edit', onClick: () => openEditModal(row) },
          { iconComponent: <IconTrash color={actionIconColor} />, text: 'Delete', onClick: () => onDelete(row) },
        ]}
      />
    ),
  },
];

const useMeasureSolutionsCriteria = () => {
  const [filters, setFilters] = useState<MeasureSolutionCriteria['filters']>(defaultMeasureSolutionCriteria.filters);
  const [sorting, setSorting] = useState<MeasureSolutionCriteria['sorting']>(defaultMeasureSolutionCriteria.sorting);
  const [pagination, setPagination] = useState<MeasureSolutionCriteria['pagination']>(defaultMeasureSolutionCriteria.pagination);

  const handleSearch = (searchQuery: string) => {
    setFilters((prev) => ({ ...prev, searchQuery }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handlePagination = (page: number, size: number) => setPagination({ page, size });

  const handleSorting = (direction, field: string) => {
    setSorting({ field, direction });
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  return { filters, sorting, pagination, handleSearch, handlePagination, handleSorting };
};

export const MeasureSolutions = () => {
  const theme = useMantineTheme();
  const { filters, sorting, pagination, handleSearch, handlePagination, handleSorting } = useMeasureSolutionsCriteria();
  const { data: { data: { measureSolutions = [], totalItems = 0 } = {} } = {}, isLoading } = useMeasureSolutions({ filters, sorting, pagination });
  const deleteMutation = useDeleteMeasureSolution();

  const columns = buildColumns(theme.colors[theme.primaryColor][6], (measureSolution) =>
    openDeleteModal(measureSolution, () => deleteMutation.mutateAsync(String(measureSolution._id))),
  );

  return (
    <Stack>
      <Group justify="flex-end" mb="md">
        <Button onClick={() => openEditModal()} variant="outline" size="lg">
          Add new
        </Button>
        <SearchBox placeholder="Search" handleSearch={handleSearch} value={filters?.searchQuery} />
      </Group>
      <Table
        columns={columns}
        dataSource={measureSolutions}
        total={totalItems}
        isLoading={isLoading}
        onPaginationChange={handlePagination}
        onSortingChange={handleSorting}
        defaultPage={pagination.page}
        defaultPerPage={pagination.size}
        defaultSorting={sorting.field}
        defaultSortingDirection={sorting.direction}
        verticalSpacing="xs"
      />
    </Stack>
  );
};
